import { v5 as uuidv5 } from 'uuid';
import type { AdvisoryRawDoc } from '../../../services/advisoryService';

export type AdvisorySourceOriginV01 = 'retrieved' | 'computed' | 'unknown';

export type AdvisorySourceKindV01 = 'document' | 'web' | 'reflection' | 'computed' | 'unknown';

export type AdvisoryClaimResolutionStateV01 = 'sourced' | 'open' | 'inconclusive' | 'not_attempted';

/**
 * A source reference contains the producer's identity and locator fields.
 * Answer quotes are explicit evidence, never a whole-answer verification verdict.
 * Opening research stays a compact register without quotes or source_uri.
 */
export interface AdvisorySourceReferenceV01 {
  /** Row identity includes origin; an unidentified passage is never merged with another. */
  key: string;
  id: string | null;
  kind: AdvisorySourceKindV01;
  canonicalId: string | null;
  compositeId: string | null;
  documentId: string | null;
  chunkId: string | null;
  title: string | null;
  locator: string | null;
  pageRange: readonly [number, number] | null;
  collectionIds: readonly string[] | null;
  origin: AdvisorySourceOriginV01;
  quotes?: readonly AdvisorySourceQuote[];
  quotesUnavailable?: boolean;
  /**
   * The producer cut this quote list at its own ceiling, and how many anchors it
   * cut. Distinct from `quotesUnavailable`, which is about entries this mapper
   * could not read: one says the list arrived short, the other says it arrived
   * damaged. A reader seeing neither is looking at every anchor there was.
   *
   * `false` / `0` is "nothing was cut", not "unknown" — the producer omits both
   * keys rather than sending a negative.
   */
  quotesTruncated?: boolean;
  quotesDropped?: number;
  /** Presentation only: the answers whose register contributed this row. */
  answerKeys?: readonly string[];
}

// Saved source names and URLs are lookup-only metadata. Keep credentials, paths
// and query strings out of serializable source objects and selection keys.
const sourceAliases = new WeakMap<AdvisorySourceReferenceV01, Set<string>>();

export function matchesAdvisorySourceUrl(source: AdvisorySourceReferenceV01, value: string) {
  return /^https?:/i.test(value) && (sourceAliases.get(source)?.has(value) ?? false);
}

export function matchesAdvisorySourceName(source: AdvisorySourceReferenceV01, value: string) {
  return (
    source.title === value ||
    (!/^https?:/i.test(value) && (sourceAliases.get(source)?.has(value) ?? false))
  );
}

/** An unidentified row must lose selection when the citation occupying it changes. */
export function advisorySourceSelectionKey(source: AdvisorySourceReferenceV01): string {
  if (source.canonicalId || source.compositeId) return source.key;
  // Include lookup-only aliases: two URLs can have identical displayed metadata.
  // Hash the snapshot so private URL details never become DOM selection keys.
  return uuidv5(
    JSON.stringify([source, Array.from(sourceAliases.get(source) ?? []).sort()]),
    uuidv5.URL
  );
}

export interface AdvisorySourceQuote {
  text: string;
  status: 'verified' | 'absent' | 'unchecked' | 'unknown';
  segmentIndex: number | null;
}

/** Presentation metadata; no answer wire field is assumed before the backend adds one. */
export interface AdvisorySourceCompleteness {
  truncated: boolean | null;
  citationsOnWire: number | null;
}

export interface AdvisoryEvidenceRegisterV01 {
  sources: readonly AdvisorySourceReferenceV01[];
  citationsOnWire: number | null;
  /** `null` means the producer did not record whether its source list was capped. */
  truncated: boolean | null;
  malformedSources: number;
}

export interface AdvisoryClaimResolutionV01 {
  state: AdvisoryClaimResolutionStateV01;
}

export type AdvisoryAnswerEvidenceValidation =
  | { ok: true; value: AdvisoryEvidenceRegisterV01 }
  | { ok: false; issue: 'invalid-answer-citations' };

export interface AdvisoryResearchContextV01 {
  register: AdvisoryEvidenceRegisterV01;
  claimResolution: AdvisoryClaimResolutionV01 | null;
}

export type AdvisoryResearchContextValidation =
  | { ok: true; value: AdvisoryResearchContextV01 }
  | { ok: false; issue: 'invalid-research-context' };

/** The shared object guard for every advisory wire payload. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const nonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const finiteNonNegativeInteger = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;

/** A page span is a pair of safe integers, 1-based and in order. */
export const advisoryPageRange = (
  start: unknown,
  end: unknown
): readonly [number, number] | null => {
  const first = finiteNonNegativeInteger(start);
  const last = finiteNonNegativeInteger(end);
  return first === null || last === null || first < 1 || last < first ? null : [first, last];
};

const pageRange = (value: unknown): readonly [number, number] | null =>
  Array.isArray(value) && value.length === 2 ? advisoryPageRange(value[0], value[1]) : null;

const collectionIds = (value: unknown): readonly string[] | null => {
  if (value === null || value === undefined) return null;
  if (!Array.isArray(value)) return null;
  const values = value.map(nonEmptyString);
  return values.every((candidate): candidate is string => candidate !== null)
    ? Array.from(new Set(values))
    : null;
};

const origin = (value: unknown): AdvisorySourceOriginV01 =>
  value === 'retrieved' || value === 'computed' ? value : 'unknown';

const sourceKind = (value: unknown): AdvisorySourceKindV01 =>
  value === 'document' || value === 'web' || value === 'reflection' || value === 'computed'
    ? value
    : 'unknown';

/** HTTP(S) sources show their site name, without links, paths, credentials or queries. */
const sourceLabel = (value: string | null): string | null => {
  if (!value || !/^https?:/i.test(value)) return value;
  try {
    return new URL(value).hostname || null;
  } catch {
    return null;
  }
};

const isClaimResolutionState = (value: unknown): value is AdvisoryClaimResolutionStateV01 =>
  value === 'sourced' || value === 'open' || value === 'inconclusive' || value === 'not_attempted';

const mapClaimResolution = (value: unknown): AdvisoryClaimResolutionV01 | null | undefined => {
  if (value === undefined || value === null) return null;
  if (!isRecord(value) || !isClaimResolutionState(value.state)) return undefined;
  return { state: value.state };
};

interface SourceFieldNames {
  kind?: string;
  source?: string;
  locator?: string;
  canonicalId: string;
  compositeId: string;
  documentId: string;
  chunkId: string;
  title: string;
  pageRange: string;
  collectionIds: string;
  origin: string;
  quotes?: string;
  quotesTruncated?: string;
  quotesDropped?: string;
}

const ANSWER_SOURCE_FIELDS: SourceFieldNames = {
  kind: 'kind',
  source: 'source',
  locator: 'locator',
  canonicalId: 'canonicalId',
  compositeId: 'compositeId',
  documentId: 'docId',
  chunkId: 'chunkId',
  title: 'docTitle',
  pageRange: 'pageRange',
  collectionIds: 'collectionIds',
  origin: 'origin',
  quotes: 'quoteSpans',
  quotesTruncated: 'quoteSpansTruncated',
  quotesDropped: 'quoteSpansDropped',
};

const V3_SOURCE_FIELDS: SourceFieldNames = {
  canonicalId: 'canonical_id',
  compositeId: 'composite_id',
  documentId: 'doc_id',
  chunkId: 'chunk_id',
  title: 'doc_title',
  pageRange: 'page_range',
  collectionIds: 'collection_ids',
  origin: 'origin',
  // The V3 register maps no `quotes` field — pgms keeps the anchor text on the
  // node documents — but it does carry the producer's statement that the list
  // was cut, and that is worth reading even where the text is not.
  quotesTruncated: 'quote_spans_truncated',
  quotesDropped: 'quote_spans_dropped',
};

function mapSource(
  raw: unknown,
  fields: SourceFieldNames,
  index: number
): AdvisorySourceReferenceV01 | null {
  if (!isRecord(raw)) return null;
  const canonicalId = nonEmptyString(raw[fields.canonicalId]);
  const compositeId = nonEmptyString(raw[fields.compositeId]);
  const documentId = nonEmptyString(raw[fields.documentId]);
  const id = canonicalId ?? compositeId ?? documentId;
  const source = fields.source ? nonEmptyString(raw[fields.source]) : null;
  const title = nonEmptyString(raw[fields.title]);
  if (!id && !source) return null;
  // The V3 register contains document passages. Answer citations state their
  // own kind; older citations without it remain unknown, even with a filename.
  const kind = fields.kind ? sourceKind(raw[fields.kind]) : 'document';
  const sourceOrigin = origin(raw[fields.origin]);
  const passageId = canonicalId ?? compositeId;
  const key = passageId ? JSON.stringify([passageId, kind, sourceOrigin]) : `unidentified-${index}`;
  const mapped: AdvisorySourceReferenceV01 = {
    key,
    id,
    kind,
    canonicalId,
    compositeId,
    documentId,
    chunkId: nonEmptyString(raw[fields.chunkId]),
    title: sourceLabel(title ?? source),
    locator: fields.locator ? sourceLabel(nonEmptyString(raw[fields.locator])) : null,
    pageRange: pageRange(raw[fields.pageRange]),
    collectionIds: collectionIds(raw[fields.collectionIds]),
    origin: sourceOrigin,
    ...(fields.quotes ? mapQuotes(raw[fields.quotes], raw.quote) : {}),
    ...mapQuoteTrim(raw, fields),
  };
  const aliases = [source, title].filter(
    (alias): alias is string => alias !== null && sourceLabel(alias) !== null
  );
  if (aliases.length) {
    sourceAliases.set(mapped, new Set(aliases));
  }
  return mapped;
}

/**
 * The producer's own statement that it cut this quote list, never inferred.
 * A list that happens to sit at the producer's ceiling lost nothing, and a
 * length is not evidence of a trim — the same rule the register's `truncated`
 * already follows.
 *
 * Both keys are omitted on a complete list, so an absent flag is read as "not
 * cut" and a non-boolean as "not stated". The count is presentation only: it
 * says how many anchors are missing, so `dropped` without `truncated` is a
 * producer that counted without declaring, and still a trim.
 */
function mapQuoteTrim(
  raw: Record<string, unknown>,
  fields: SourceFieldNames
): Pick<AdvisorySourceReferenceV01, 'quotesTruncated' | 'quotesDropped'> {
  const dropped = fields.quotesDropped
    ? finiteNonNegativeInteger(raw[fields.quotesDropped]) ?? 0
    : 0;
  const truncated =
    (fields.quotesTruncated ? raw[fields.quotesTruncated] === true : false) || dropped > 0;
  return truncated ? { quotesTruncated: true, quotesDropped: dropped } : {};
}

function mapQuotes(
  raw: unknown,
  legacyQuote?: unknown
): Pick<AdvisorySourceReferenceV01, 'quotes' | 'quotesUnavailable'> {
  // Saved answers from before quoteSpans still carry useful text. Nothing in
  // that older field tells us whether the producer checked it against a source.
  if (raw == null)
    return {
      quotes:
        typeof legacyQuote === 'string' && legacyQuote.trim()
          ? [{ text: legacyQuote, status: 'unknown', segmentIndex: null }]
          : [],
      quotesUnavailable: false,
    };
  if (!Array.isArray(raw)) return { quotes: [], quotesUnavailable: true };
  let quotesUnavailable = false;
  const quotes: AdvisorySourceQuote[] = [];
  raw.forEach(value => {
    if (!isRecord(value) || typeof value.text !== 'string' || !value.text.trim()) {
      quotesUnavailable = true;
      return;
    }
    quotes.push({
      text: value.text,
      status:
        value.status === 'verified' || value.status === 'absent' || value.status === 'unchecked'
          ? value.status
          : 'unknown',
      segmentIndex: finiteNonNegativeInteger(value.segment_index),
    });
  });
  return { quotes: uniqueQuotes(quotes), quotesUnavailable };
}

/** Match recorded passages, never filenames, equal numbers, or local row keys. */
export function sameAdvisoryPassage(
  a: AdvisorySourceReferenceV01,
  b: AdvisorySourceReferenceV01
): boolean {
  if (a.kind !== b.kind || a.origin !== b.origin) return false;
  if (a.canonicalId && b.canonicalId) return a.canonicalId === b.canonicalId;
  if (a.compositeId && b.compositeId) return a.compositeId === b.compositeId;
  return !!(a.documentId && a.chunkId && a.documentId === b.documentId && a.chunkId === b.chunkId);
}

function uniqueQuotes(quotes: readonly AdvisorySourceQuote[]): AdvisorySourceQuote[] {
  return Array.from(new Map(quotes.map(quote => [JSON.stringify(quote), quote])).values());
}

function mapRegister(raw: unknown, fields: SourceFieldNames): AdvisoryEvidenceRegisterV01 | null {
  if (!isRecord(raw) || !Array.isArray(raw.sources)) return null;
  const sources: AdvisorySourceReferenceV01[] = [];
  const seen = new Map<string, AdvisorySourceReferenceV01>();
  let malformedSources = 0;
  raw.sources.forEach((source, index) => {
    const mapped = mapSource(source, fields, index);
    if (!mapped) {
      malformedSources += 1;
      return;
    }
    const existing = seen.get(mapped.key);
    if (existing) {
      const aliases = sourceAliases.get(mapped);
      if (aliases) {
        sourceAliases.set(
          existing,
          new Set([...Array.from(sourceAliases.get(existing) ?? []), ...Array.from(aliases)])
        );
      }
      if (fields.quotes) {
        existing.quotes = uniqueQuotes([...(existing.quotes ?? []), ...(mapped.quotes ?? [])]);
        existing.quotesUnavailable = existing.quotesUnavailable || mapped.quotesUnavailable;
      }
      // Independent of `fields.quotes`: the V3 register carries the trim
      // statement without carrying the text it applies to.
      if (mapped.quotesTruncated) {
        existing.quotesTruncated = true;
        existing.quotesDropped = (existing.quotesDropped ?? 0) + (mapped.quotesDropped ?? 0);
      }
      return;
    }
    seen.set(mapped.key, mapped);
    sources.push(mapped);
  });
  const citationsOnWire = finiteNonNegativeInteger(raw.citationsOnWire ?? raw.citations_on_wire);
  const truncationValue = raw.truncated;
  return {
    sources,
    citationsOnWire,
    truncated: typeof truncationValue === 'boolean' ? truncationValue : null,
    malformedSources,
  };
}

/**
 * Reads AnswerRecord.to_dict().citations (agent-svc GL-DEV-885). This list has
 * no version, truncation flag or wire count. Optional completeness is a presentation
 * input for a future producer adapter; missing fields remain unknown.
 * Neither reflectionEvidence nor verification describes the answer's sources.
 */
export function mapAdvisoryAnswerCitations(
  input: unknown,
  completeness?: AdvisorySourceCompleteness
): AdvisoryAnswerEvidenceValidation {
  const register = mapRegister({ ...completeness, sources: input }, ANSWER_SOURCE_FIELDS);
  return register
    ? { ok: true, value: register }
    : { ok: false, issue: 'invalid-answer-citations' };
}

/**
 * Reads the two V3 roll-ups intentionally persisted on the root turn. No
 * node-level data, raw errors, source_uri, or quotes
 * cross this boundary.
 */
export function mapAdvisoryResearchContext(raw: AdvisoryRawDoc): AdvisoryResearchContextValidation {
  const metadata = raw.data.metadata;
  if (!isRecord(metadata)) return { ok: false, issue: 'invalid-research-context' };

  const docIntel = metadata.doc_intel;
  const register =
    docIntel === undefined || docIntel === null
      ? {
          sources: [],
          citationsOnWire: null,
          truncated: null,
          malformedSources: 0,
        }
      : mapRegister(docIntel, V3_SOURCE_FIELDS);
  const claimResolution = mapClaimResolution(metadata.claim_resolution);
  if (!register || claimResolution === undefined) {
    return { ok: false, issue: 'invalid-research-context' };
  }
  return { ok: true, value: { register, claimResolution } };
}
